import {
  AnalyticsPermissions,
  ConversionPermissions,
  CustomersPermissions,
  GeneralPermissions,
  ImsPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
  SupportPermissions,
  userPermissions,
  UserPermissions,
} from "./permissions";

export const ManagedUserRoles = {
  ADMIN: "admin",
  USER: "user",
  SUPPORT_REPLY_ONLY: "reply-only",
  SUPPORT_VIEW_ONLY: "view-only",
  WAREHOUSE_USER: "warehouse-user",
  WAREHOUSE_PACKER: "warehouse-packer",
  WAREHOUSE_PICKER: "warehouse-picker",
  WAREHOUSE_REPLENISHER: "warehouse-replenisher",
  IMS_FINANCE: "ims-finance",
  IMS_OPERATIONS: "ims-operations",
} as const;

export const ManagedUserRolesArray = Object.values(ManagedUserRoles);

export type ManagedUserRolesType =
  (typeof ManagedUserRoles)[keyof typeof ManagedUserRoles];

export const ROLE_PERMISSIONS: Record<
  ManagedUserRolesType,
  readonly UserPermissions[]
> = {
  // Admin gets all permissions
  [ManagedUserRoles.ADMIN]: userPermissions,

  // User role permissions
  [ManagedUserRoles.USER]: [
    GeneralPermissions.OTHER,
    GeneralPermissions.VIEW_PRODUCTS,

    AnalyticsPermissions.VIEW_ANALYTICS,
    AnalyticsPermissions.MANAGE_ANALYTICS,

    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.MANAGE_SHIPMENTS,
    OrderManagementPermissions.EDIT_ORDER,
    OrderManagementPermissions.VIEW_ORDER,
    OrderManagementPermissions.RECEIVE_ORDER_UPDATES,
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,

    ReturnsAndClaimsPermissions.CREATE_RETURN,
    ReturnsAndClaimsPermissions.RESET_RETURN,
    ReturnsAndClaimsPermissions.APPROVE_RETURN,
    ReturnsAndClaimsPermissions.PROCESS_RETURN,
    ReturnsAndClaimsPermissions.REOPEN_RETURN,
    ReturnsAndClaimsPermissions.FLAG_RETURN,
    ReturnsAndClaimsPermissions.CLOSE_RETURN,
    ReturnsAndClaimsPermissions.EDIT_RETURN_INFORMATION,
    ReturnsAndClaimsPermissions.RECEIVE_RETURN,
    ReturnsAndClaimsPermissions.MANAGE_RETURN_SHIPPING,
    ReturnsAndClaimsPermissions.MANAGE_RMA,
    ReturnsAndClaimsPermissions.RESEND_CUSTOMER_NOTIFICATIONS,
    ReturnsAndClaimsPermissions.FILE_CARRIER_CLAIM,
    ReturnsAndClaimsPermissions.CHARGE_RECOVERY_FEE,
    ReturnsAndClaimsPermissions.VIEW_RETURN,
    ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
    ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
    ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
    ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS,
    ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,

    SupportPermissions.VIEW_SUPPORT,
    SupportPermissions.CLOSE_CONVERSATION,
    SupportPermissions.ASSIGN_TAG,
    SupportPermissions.MANAGE_TAG,
    SupportPermissions.CREATE_REPLY,
    SupportPermissions.USE_MACRO,
    SupportPermissions.CREATE_MACRO,
    SupportPermissions.ARCHIVE_CONVERSATION,
    SupportPermissions.CREATE_TEAM_VIEW,
    SupportPermissions.EDIT_ASSIGNEE,
    SupportPermissions.EDIT_CUSTOMER,
    SupportPermissions.CREATE_RULE,
    SupportPermissions.DELETE_RULE,
    SupportPermissions.MONITOR_AI,
    SupportPermissions.VIEW_CUSTOMER_ORDERS,
    SupportPermissions.VIEW_CUSTOMER_RETURNS,

    MarketingPermissions.VIEW_MARKETING,
    MarketingPermissions.EDIT_LOYALTY_POINTS,
    MarketingPermissions.VIEW_LOYALTY_POINTS,

    ConversionPermissions.ACCESS_CONVERSION,

    ImsPermissions.ACCESS_INVENTORY,
    ImsPermissions.ACCESS_CYCLE_COUNTS,
    ImsPermissions.MANAGE_CYCLE_COUNT_RULES,
    ImsPermissions.ACCESS_FORECASTING,
    ImsPermissions.ACCESS_SCENARIO_PLANNING,
    ImsPermissions.ACCESS_SUPPLIERS,
    ImsPermissions.ACCESS_BILL_OF_MATERIALS,
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
    ImsPermissions.ACCESS_SHIPMENTS,
    ImsPermissions.ACCESS_TRANSFERS,
    ImsPermissions.VIEW_REPORTS,
    ImsPermissions.ACCESS_INTELLIGENT_INVENTORY,

    CustomersPermissions.VIEW_CUSTOMER_RECORD,
  ],

  // Reply only permissions
  [ManagedUserRoles.SUPPORT_REPLY_ONLY]: [
    SupportPermissions.VIEW_SUPPORT,
    SupportPermissions.CLOSE_CONVERSATION,
    SupportPermissions.ASSIGN_TAG,
    SupportPermissions.MANAGE_TAG,
    SupportPermissions.CREATE_REPLY,
    SupportPermissions.USE_MACRO,
    SupportPermissions.EDIT_ASSIGNEE,
    SupportPermissions.EDIT_CUSTOMER,
    SupportPermissions.VIEW_CUSTOMER_ORDERS,
    SupportPermissions.VIEW_CUSTOMER_RETURNS,

    MarketingPermissions.VIEW_LOYALTY_POINTS,

    GeneralPermissions.VIEW_PRODUCTS,
  ],

  // View only permissions
  [ManagedUserRoles.SUPPORT_VIEW_ONLY]: [
    SupportPermissions.VIEW_SUPPORT,
    SupportPermissions.VIEW_CUSTOMER_ORDERS,
    SupportPermissions.VIEW_CUSTOMER_RETURNS,

    MarketingPermissions.VIEW_LOYALTY_POINTS,

    GeneralPermissions.VIEW_PRODUCTS,
  ],

  [ManagedUserRoles.WAREHOUSE_USER]: [
    OrderManagementPermissions.MANAGE_SHIPMENTS,
    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.RECEIVE_ORDER_UPDATES,
    OrderManagementPermissions.MANAGE_PICK_SESSIONS,
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,

    ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,

    ImsPermissions.ACCESS_CYCLE_COUNTS,

    GeneralPermissions.VIEW_PRODUCTS,
  ],
  [ManagedUserRoles.WAREHOUSE_PACKER]: [
    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.RECEIVE_ORDER_UPDATES,
    OrderManagementPermissions.MANAGE_PICK_SESSIONS,
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,

    ImsPermissions.ACCESS_CYCLE_COUNTS,

    GeneralPermissions.VIEW_PRODUCTS,
  ],
  [ManagedUserRoles.WAREHOUSE_PICKER]: [
    OrderManagementPermissions.RECEIVE_ORDER_UPDATES,
    OrderManagementPermissions.MANAGE_PICK_SESSIONS,
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,

    ImsPermissions.ACCESS_CYCLE_COUNTS,

    GeneralPermissions.VIEW_PRODUCTS,
  ],
  [ManagedUserRoles.WAREHOUSE_REPLENISHER]: [
    OrderManagementPermissions.RECEIVE_ORDER_UPDATES,
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,

    GeneralPermissions.VIEW_PRODUCTS,
  ],

  // IMS: Finance — full IMS access, scoped to finance-specific views in the future
  [ManagedUserRoles.IMS_FINANCE]: [
    GeneralPermissions.OTHER,
    GeneralPermissions.VIEW_PRODUCTS,

    AnalyticsPermissions.VIEW_ANALYTICS,
    AnalyticsPermissions.MANAGE_ANALYTICS,

    ImsPermissions.ACCESS_INVENTORY,
    ImsPermissions.ACCESS_CYCLE_COUNTS,
    ImsPermissions.MANAGE_CYCLE_COUNT_RULES,
    ImsPermissions.ACCESS_FORECASTING,
    ImsPermissions.ACCESS_SCENARIO_PLANNING,
    ImsPermissions.ACCESS_SUPPLIERS,
    ImsPermissions.ACCESS_BILL_OF_MATERIALS,
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
    ImsPermissions.ACCESS_SHIPMENTS,
    ImsPermissions.ACCESS_TRANSFERS,
    ImsPermissions.VIEW_REPORTS,
    ImsPermissions.ACCESS_INTELLIGENT_INVENTORY,
  ],

  // IMS: Operations — hands-on warehouse work, no sensitive business metrics
  [ManagedUserRoles.IMS_OPERATIONS]: [
    GeneralPermissions.OTHER,
    GeneralPermissions.VIEW_PRODUCTS,

    AnalyticsPermissions.VIEW_ANALYTICS,
    AnalyticsPermissions.MANAGE_ANALYTICS,

    ImsPermissions.ACCESS_INVENTORY,
    ImsPermissions.ACCESS_CYCLE_COUNTS,
    ImsPermissions.ACCESS_BILL_OF_MATERIALS,
    ImsPermissions.ACCESS_SHIPMENTS,
    ImsPermissions.ACCESS_TRANSFERS,
  ],
};

export const managedUserRolesToFriendlyName: Record<
  ManagedUserRolesType,
  string
> = {
  [ManagedUserRoles.ADMIN]: "Admin",
  [ManagedUserRoles.USER]: "User",
  [ManagedUserRoles.SUPPORT_REPLY_ONLY]: "Support: Reply Only",
  [ManagedUserRoles.SUPPORT_VIEW_ONLY]: "Support: View Only",
  [ManagedUserRoles.WAREHOUSE_USER]: "Order Management: Warehouse User",
  [ManagedUserRoles.WAREHOUSE_PACKER]: "Order Management: Warehouse Packer",
  [ManagedUserRoles.WAREHOUSE_PICKER]: "Order Management: Warehouse Picker",
  [ManagedUserRoles.WAREHOUSE_REPLENISHER]:
    "Order Management: Warehouse Replenisher",
  [ManagedUserRoles.IMS_FINANCE]: "IMS: Finance",
  [ManagedUserRoles.IMS_OPERATIONS]: "IMS: Operations",
};

/**
 * Maps user roles to managed user roles
 * Add/modify this mapping based on your role requirements
 */
export const ROLE_TO_MANAGED_ROLE_MAP: Record<string, ManagedUserRolesType> = {
  admin: ManagedUserRoles.ADMIN,
  user: ManagedUserRoles.USER,
  "reply-only": ManagedUserRoles.SUPPORT_REPLY_ONLY,
  "view-only": ManagedUserRoles.SUPPORT_VIEW_ONLY,
};

export const MANAGED_ROLE_TO_ROLE_MAP: { [key: string]: string } = {
  [ManagedUserRoles.ADMIN]: "admin",
  [ManagedUserRoles.USER]: "user",
  [ManagedUserRoles.SUPPORT_REPLY_ONLY]: "reply-only",
  [ManagedUserRoles.SUPPORT_VIEW_ONLY]: "view-only",
};

/**
 * Managed roles that have legacy equivalents
 */
export const OVERLAPPING_MANAGED_ROLES = new Set<ManagedUserRolesType>([
  ManagedUserRoles.ADMIN,
  ManagedUserRoles.USER,
  ManagedUserRoles.SUPPORT_REPLY_ONLY,
  ManagedUserRoles.SUPPORT_VIEW_ONLY,
]);

/**
 * Converts user roles to managed user roles
 */
export function mapRolesToManagedUserRoles(
  roles: string[] | undefined | null,
): ManagedUserRolesType[] {
  if (!roles) {
    return [];
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return roles
    .map((role) => {
      if (Object.keys(ROLE_TO_MANAGED_ROLE_MAP).includes(role)) {
        return ROLE_TO_MANAGED_ROLE_MAP[role];
      }
      return null;
    })
    .filter((role) => role !== null);
}

export function syncManagedUserRolesToRoles(
  managedRoles: ManagedUserRolesType[],
  initialRoles: string[],
): string[] {
  const nonManagedRoles = initialRoles.filter(
    (role) => !ROLE_TO_MANAGED_ROLE_MAP[role],
  );
  const syncedMappedRoles = managedRoles.map(
    (role) => MANAGED_ROLE_TO_ROLE_MAP[role],
  );
  return [...nonManagedRoles, ...syncedMappedRoles].filter(
    (role) => role != null,
  );
}
